import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Added useNavigate
import { useTheme } from '../../Context/ThemeContext';
import { useRole } from '../../Context/RoleContext';
import Navbar from '../../Components/Navbar';
import FormInput from '../../Components/AuthForm/FormInput';
import PasswordInput from '../../Components/AuthForm/PasswordInput';
import { AuthApi } from '../../Api/BaseApi';
import { useChatContext } from '../../Context/ChatContext'; // Import ChatContext

import './index.css';

function Login() {
  const { isDarkMode } = useTheme();
  const { refreshUserRole } = useRole();
  const { handleLoginSuccess } = useChatContext(); // Get the login success handler from context

  const navigate = useNavigate(); // Hook for navigation
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });

  const [isLoading, setIsLoading] = useState(false); // For loading state

  // Prefilled mailto link for Forgot Password
  const mailSubject = encodeURIComponent('Password reset request for Pinaka.live account');
  const mailBody = encodeURIComponent(
    `Hello Admin,\n\n` +
    `I would like to request a password reset for my account.\n\n` +
    `Details:\n` +
    `- Full Name: __________________________\n` +
    `- Registered Email: ___________________\n` +
    `- Organization/Team: _________________\n` +
    `- Additional Info: ___________________\n\n` +
    `Thank you,\n`
  );
  const forgotMailHref = `mailto:contactus@pinakaai.com?subject=${mailSubject}&body=${mailBody}`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: '', password: '', general: '' };

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      valid = false;
    }


    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => { // Made async
    e.preventDefault();
    setErrors(prev => ({ ...prev, general: '' }));

    if (!validateForm()) {
      // No general error here, specific field errors are shown
      return;
    }

    setIsLoading(true);
    try {
      const response = await AuthApi.login(formData.email, formData.password);

      // Assuming AuthApi.login stores token and user_info in cookies
      // and returns { success: true, token, user }
      // if (response.success) {
        // Optional: You might want to update a global auth context here
        handleLoginSuccess(response.user); // Notify context about successful login and user data
        refreshUserRole(); // Refresh role context to show admin functions immediately
        navigate('/'); // Or to the 'from' location
      // } else {
      //   // This case might not be hit if AuthApi.login throws for non-success
      //   setErrors(prev => ({ ...prev, general: response.error || 'Login failed. Please try again.' }));
      // }
    } catch (error) {
      console.error('Login API error:', error);
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      if (error.response && error.response.data) {
        // Attempt to get a more specific error message from the backend
        const backendError = error.response.data;
        if (typeof backendError.detail === 'string') {
          errorMessage = backendError.detail;
        } else if (typeof backendError.error === 'string') {
            errorMessage = backendError.error;
        } else if (backendError.non_field_errors && backendError.non_field_errors.length > 0) {
            errorMessage = backendError.non_field_errors.join(' ');
        }
        // You could also check for field-specific errors if your backend provides them
        // e.g., if (backendError.email) setErrors(prev => ({ ...prev, email: backendError.email[0] }));
      }
      setErrors(prev => ({ ...prev, general: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className={`auth-page-container ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="auth-layout">
          <div className="auth-image-section">
            <img
              src="/assets/login-hero.jpg"
              alt="Pinaka AI — predictive accounts receivable"
              loading="lazy"
              onError={(e) => {
                // Never leave the panel blank: fall back to the bundled logo, then hide.
                if (e.currentTarget.src.indexOf('logo.png') === -1) {
                  e.currentTarget.src = '/assets/logo.png';
                } else {
                  e.currentTarget.style.display = 'none';
                }
              }}
            />
            <div className="image-overlay">
              <div className="overlay-content">
                <h2>Welcome Back!</h2>
                <p>Log in to access your personalized experience and continue where you left off.</p>
              </div>
            </div>
          </div>

          <div className="auth-form-section">
            <div className="auth-header">
              {/* <h1>Sign In</h1> */}
              <h1 style={{ fontSize: '1.5rem', fontWeight: '700', letterSpacing: '-0.025em' }}>Already a user? Sign in.</h1>
              <p>Enter your credentials to access your Pinaka AI account.</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <FormInput
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                error={errors.email}
                autoComplete="email"
                disabled={isLoading}
              />

              <PasswordInput
                id="password"
                name="password"
                label="Password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                error={errors.password}
                autoComplete="current-password"
                disabled={isLoading}
              />

              <div className="form-options">
                <label className="remember-me">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <span>Remember me</span>
                </label>
                <a href={forgotMailHref} className="forgot-password-link">Forgot Password?</a>
              </div>

              {errors.general && (
                <p className="error-message general-error" aria-live="polite">
                  {errors.general}
                </p>
              )}

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-footer">
              <span>Need access? Contact your administrator.</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;